import React from "react";
import { useMemo } from "react";
import PropTypes from "prop-types";
import { currentShift, realOrSimulatedNow } from "../../shift";
import { createWeeksToRender } from "./calendar_weeks";

export const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export function formatDateToMonthDay(date, excludeYear) {
  const options = { month: "long", day: "numeric" };
  const localDateString = date.toLocaleDateString("en-US", options);
  return excludeYear ? `${localDateString}` : `${localDateString}, ${date.getFullYear()}`;
}

const CalendarComponent = ({ month, year, displayForDatesFunc, useShiftDate = false }) => {
  const weeksToRender = useMemo(() => {
    return createWeeksToRender(month, year);
  }, [month, year]);
  // Shift-anchored surfaces (shift logs) mark the SHIFT date as "today": during
  // the overnight 3rd shift (23:00-06:59) that is still the date the shift
  // began. Calendar-date surfaces (appointments) keep plain calendar-today.
  // currentShift().date is a "YYYY-MM-DD" string in the facility time zone;
  // compare it against each cell's formatted key rather than constructing a
  // Date from it, which could shift across zones.
  const currentDayKey = useShiftDate ? currentShift().date : formatDate(realOrSimulatedNow());

  return (
    <table className="ccw-month-calendar">
      <thead>
        <tr>
          {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map(
            (weekday) => (
              <th key={weekday} scope="col">
                <abbr title={weekday}>{weekday}</abbr>
              </th>
            )
          )}
        </tr>
      </thead>
      <tbody>
        {weeksToRender.map((days, rowIndex) => (
          <tr key={rowIndex}>
            {days.map((day) => {
              const Display = displayForDatesFunc(day);
              const isCurrentDay = formatDate(day) === currentDayKey;
              const isOutsideMonth = day.getMonth() !== month;
              const cellClassName = [
                isOutsideMonth ? "is-outside-month" : "",
                isCurrentDay ? "current-day is-current-day" : ""
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <td
                  className={cellClassName}
                  id={isCurrentDay ? "current-day" : ""}
                  key={formatDate(day)}
                >
                  <div className="ccw-month-calendar__day">
                    <div className="ccw-month-calendar__date">
                      <span className="ccw-month-calendar__weekday">
                        {day.toLocaleDateString("en-US", { weekday: "short" })}
                      </span>
                      <span>{formatDateToMonthDay(day, true)}</span>
                      {isCurrentDay && useShiftDate && (
                        <span className="ccw-month-calendar__current-label">Current shift</span>
                      )}
                    </div>
                    <div className="ccw-month-calendar__events">{Display}</div>
                  </div>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

CalendarComponent.propTypes = {
  month: PropTypes.number,
  year: PropTypes.number,
  displayForDatesFunc: PropTypes.func,
  displayForDates: PropTypes.object,
  // Opt-in: mark the current SHIFT date (not the calendar date) as
  // "current-day". Only shift-anchored consumers (shift logs) should set this.
  useShiftDate: PropTypes.bool
};

export default CalendarComponent;
